import React from "react";
import { motion } from "framer-motion";
import { Users } from "lucide-react";
import { FLEET } from "@/data";
import { Reveal, Overline } from "@/components/Reveal";

export const Fleet = () => (
  <section id="fleet" className="scroll-mt-24 bg-background py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
      <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Reveal>
            <Overline>Our Fleet</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-white md:text-5xl">
              Immaculate vehicles, <span className="italic text-primary">every mile.</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="max-w-sm text-sm font-light leading-relaxed text-zinc-400">
            From executive sedans to full-size motorcoaches — a vehicle for every occasion, maintained to showroom standard.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {FLEET.map((v, i) => (
          <motion.div
            key={v.name}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`fleet-${i}`}
            className="group relative overflow-hidden border border-white/5 bg-zinc-950"
          >
            <div className="absolute right-5 top-5 z-20 border border-white/15 bg-black/50 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-primary backdrop-blur">
              {v.klass}
            </div>
            <div
              className="relative flex aspect-[16/11] items-center justify-center overflow-hidden px-8"
              style={{
                background:
                  "radial-gradient(120% 90% at 50% 8%, #2a2a2e 0%, #17171a 42%, #0c0c0e 100%)",
              }}
            >
              {/* floor */}
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-[30%] left-0 right-0 h-px bg-white/10" />
              {/* gold spotlight glow */}
              <div className="absolute left-1/2 top-1/2 h-52 w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl transition-all duration-700 group-hover:bg-primary/20" />
              <img
                src={v.image}
                alt={v.name}
                loading="lazy"
                className="relative z-[1] max-h-[78%] w-full object-contain transition-transform duration-700 group-hover:scale-[1.06]"
                style={{
                  WebkitBoxReflect:
                    "below 1px linear-gradient(to bottom, transparent 55%, rgba(255,255,255,0.12))",
                  filter: "drop-shadow(0 30px 22px rgba(0,0,0,0.65))",
                }}
              />
            </div>
            <div className="border-t border-white/5 bg-zinc-950 p-7">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-serif text-2xl font-light text-white">{v.name}</h3>
                <span className="flex shrink-0 items-center gap-1.5 text-xs text-zinc-500">
                  <Users className="h-3.5 w-3.5 text-primary" /> {v.seats.replace("Up to ", "").replace(" Passengers", "")}
                </span>
              </div>
              <p className="mt-3 text-sm font-light leading-relaxed text-zinc-400">{v.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
